import { useCallback, useEffect, useRef, useState } from 'react';

interface StatusMessage {
  title: string;
  description: string;
  type: 'success' | 'error';
  permission?: boolean;
  subscription?: boolean;
}

interface SubscriptionDetails {
  p256dhKey?: string;
  authKey?: string;
}

interface UseNotificationsReturn {
  isSubscribed: boolean;
  isLoading: boolean;
  status: StatusMessage | null;
  subscriptionDetails: SubscriptionDetails | null;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
  clearStatus: () => void;
}

interface UseNotificationsOptions {
  vapidPublicKey: string;
  onSubscriptionChange?: (subscription: PushSubscription | null) => void;
}

export const useNotifications = ({
  vapidPublicKey,
  onSubscriptionChange,
}: UseNotificationsOptions): UseNotificationsReturn => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<StatusMessage | null>({
    type: 'success',
    title: 'Enable Notifications',
    description: 'Enable notifications and stay up-to-date',
  });
  const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionDetails | null>(
    null
  );
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);

  const clearStatus = useCallback(() => {
    setStatus(null);
  }, []);

  const checkSupport = (): boolean => {
    if (!('serviceWorker' in navigator)) {
      setStatus({
        title: 'Not Supported',
        description: 'Your browser does not support push notifications',
        type: 'error',
      });
      return false;
    }

    if (!('PushManager' in window)) {
      setStatus({
        title: 'Not Supported',
        description: 'Push messaging is not supported in your browser',
        type: 'error',
      });
      return false;
    }

    return true;
  };

  const getServiceWorkerRegistration = async (): Promise<ServiceWorkerRegistration | null> => {
    try {
      if (registrationRef.current) {
        return registrationRef.current;
      }

      const registration = await navigator.serviceWorker.ready;
      registrationRef.current = registration;
      return registration;
    } catch {
      setStatus({
        title: 'Service Worker Error',
        description: 'Failed to get service worker registration',
        type: 'error',
      });
      return null;
    }
  };

  const subscribe = useCallback(async (): Promise<void> => {
    if (!checkSupport()) return;

    setIsLoading(true);
    clearStatus();

    try {
      const permission = await Notification.requestPermission();

      if (permission !== 'granted') {
        setStatus({
          title: 'Permission Denied',
          description: 'Please allow notifications in your browser settings',
          type: 'error',
        });
        return;
      }

      const registration = await getServiceWorkerRegistration();
      if (!registration) return;

      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        setIsSubscribed(true);
        onSubscriptionChange?.(existingSubscription);
        setStatus({
          title: 'Already Subscribed',
          description: 'You are already subscribed to notifications',
          type: 'success',
          permission: true,
          subscription: false,
        });
        return;
      }

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidPublicKey,
      });

      setIsSubscribed(true);
      onSubscriptionChange?.(subscription);
      setStatus({
        title: 'Notifications Enabled',
        description: 'You will now receive push notifications',
        type: 'success',
        permission: true,
        subscription: true,
      });
    } catch (error: unknown) {
      const e = error as Error;
      if (e.name === 'NotSupportedError') {
        setStatus({
          title: 'Not Supported',
          description: 'Push notifications are not supported on this device',
          type: 'error',
        });
      } else if (e.name === 'NotAllowedError') {
        setStatus({
          title: 'Permission Blocked',
          description: 'Notifications are blocked. Please enable them in browser settings',
          type: 'error',
        });
      } else if (e.message?.includes('gcm_sender_id')) {
        setStatus({
          title: 'Configuration Error',
          description:
            'Please enable "Use Google services for push messaging" in your browser',
          type: 'error',
        });
      } else if (e.name === 'AbortError') {
        setStatus({
          title: 'Subscription Notifications Failed',
          description:
            'Notifications permission is granted but notifications subscription are blocked by browser. Please enable push messaging in browser settings',
          type: 'error',
        });
      } else {
        setStatus({
          title: 'Subscription Failed',
          description: 'Failed  to subscribe to notifications. Please try again',
          type: 'error',
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [vapidPublicKey, onSubscriptionChange]);

  const unsubscribe = useCallback(async (): Promise<void> => {
    if (!checkSupport()) return;

    setIsLoading(true);
    clearStatus();

    try {
      const registration = await getServiceWorkerRegistration();
      if (!registration) return;

      const subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        setStatus({
          title: 'Not Subscribed',
          description: 'You are not currently subscribed to notifications',
          type: 'error',
        });
        return;
      }

      const successful = await subscription.unsubscribe();

      if (successful) {
        setIsSubscribed(false);
        onSubscriptionChange?.(null);
        setStatus({
          title: 'Unsubscribed',
          description: 'You will no longer receive push notifications',
          type: 'success',
        });
      } else {
        setStatus({
          title: 'Unsubscribe Failed',
          description: 'Failed to unsubscribe from notifications',
          type: 'error',
        });
      }
    } catch {
      setStatus({
        title: 'Unsubscribe Failed',
        description: 'An error occurred while unsubscribing',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [onSubscriptionChange]);

  const checkInitialSubscription = useCallback(async (): Promise<void> => {
    if (!checkSupport()) return;

    try {
      const registration = await getServiceWorkerRegistration();
      if (!registration) return;

      const permission = Notification.permission;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        setIsSubscribed(true);
        setStatus({
          title: 'Notifications Active',
          description: 'You are subscribed to push notifications',
          type: 'success',
          permission: true,
          subscription: true,
        });
        const subscriptionJson = subscription.toJSON();

        setSubscriptionDetails({
          p256dhKey: subscriptionJson.keys?.p256dh,
          authKey: subscriptionJson.keys?.auth,
        });
      } else {
        setIsSubscribed(false);

        if (permission === 'granted') {
          setStatus({
            title: 'Ready to Subscribe',
            description: 'Notifications are allowed but not yet subscribed',
            type: 'success',
          });
        } else if (permission === 'denied') {
          setStatus({
            title: 'Notifications Blocked',
            description: 'Please enable notifications in your browser settings',
            type: 'error',
          });
        } else if (permission === 'default') {
          setStatus({
            title: 'Enable Notifications',
            description: 'Enable notifications and stay up-to-date',
            type: 'success',
          });
        }
      }
    } catch {
      setIsSubscribed(false);
      setStatus({
        title: 'Check Failed',
        description: 'Unable to check notification status',
        type: 'error',
      });
    }
  }, []);

  useEffect(() => {
    checkInitialSubscription().then();
  }, [checkInitialSubscription]);

  return {
    isSubscribed,
    isLoading,
    status,
    subscriptionDetails,
    subscribe,
    unsubscribe,
    clearStatus,
  };
};
